import React from "react";
import {
  Ban,
  Bike,
  Check,
  ChefHat,
  CircleCheck,
  Copy,
  Eye,
  Hourglass,
  Loader2,
  MessageCircle,
  MoreVertical,
  PackageCheck,
  Printer,
  Send,
  Store,
  Ticket,
  Truck,
  User,
  Wallet,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import orderService from "@/services/orderService";
import { cn } from "@/lib/utils";
import { formatCurrency, formatElapsed } from "@/lib/format";
import {
  FLOW,
  LATE_AFTER_MINUTES,
  NEXT_STEP,
  OPEN_STATUSES,
  countItems,
  getElapsedMinutes,
  getProgress,
  getStageLabel,
  getStatusLabel,
  isDelivery,
  summarizeItems,
} from "./orderHelpers";

/** Pílula de status e cor da barra lateral do card */
const STATUS_STYLES = {
  Aguardando: {
    pill: "border-terra/25 bg-terra-soft text-terra",
    accent: "bg-terra",
    icon: Hourglass,
  },
  Preparando: {
    pill: "border-transparent bg-primary text-primary-foreground",
    accent: "bg-primary",
    pulse: true,
  },
  Liberado: {
    pill: "border-brand-pistachio/50 bg-brand-pistachio/20 text-brand-dark",
    accent: "bg-brand-pistachio",
    icon: Bike,
  },
  Entregue: {
    pill: "border-border bg-muted text-foreground",
    accent: "bg-primary/40",
    icon: PackageCheck,
  },
  Finalizado: {
    pill: "border-border bg-muted text-muted-foreground",
    accent: "bg-border",
    icon: CircleCheck,
  },
  Cancelado: {
    pill: "border-destructive/20 bg-destructive/10 text-destructive",
    accent: "bg-destructive/50",
    icon: Ban,
  },
};

const STEP_ICONS = {
  Preparando: ChefHat,
  Liberado: Bike,
  Entregue: PackageCheck,
  Finalizado: CircleCheck,
};

const iconButton =
  "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-muted-foreground transition-colors hover:bg-muted hover:text-primary active:scale-95 disabled:pointer-events-none disabled:opacity-60";

/**
 * Card do pedido (docs/tela/code.html): código + status + pagamento, total,
 * cliente/entrega/tempo, prévia dos itens, etapa com progresso e ações.
 */
const OrderListCard = ({
  order,
  now,
  busy = false,
  onChangeStatus,
  onCancel,
  onDetails,
  onPayment,
  onWhatsApp,
  onPrint,
  onStorePrinter,
  onCopyText,
}) => {
  const style = STATUS_STYLES[order.status] || STATUS_STYLES.Finalizado;
  const StatusIcon = style.icon;
  const delivery = isDelivery(order);
  const open = OPEN_STATUSES.includes(order.status);
  const closed = order.status === "Finalizado" || order.status === "Cancelado";
  const next = NEXT_STEP[order.status];
  const NextIcon = next ? STEP_ICONS[next.status] : Check;
  const progress = getProgress(order);
  const elapsed = getElapsedMinutes(order, now);
  const late = open && elapsed !== null && elapsed >= LATE_AFTER_MINUTES;
  const createdAt = orderService.parseDateTime(order.date_time);
  const itemCount = countItems(order);
  const items = summarizeItems(order);
  const freight = parseFloat(order.freight_value) || 0;

  return (
    <article
      className={cn(
        "relative overflow-hidden rounded-xl border bg-card shadow-soft transition-shadow hover:shadow-ambient",
        order.status === "Preparando" && "border-primary/30",
        closed && "opacity-90",
      )}
    >
      <span className={cn("absolute inset-y-0 left-0 w-1.5", style.accent)} aria-hidden="true" />

      <div className="flex flex-col gap-2 py-3 pl-4 pr-3.5">
        {/* Código, status, pagamento e total */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="font-display text-base font-bold tracking-tight text-primary">
              #{order.code}
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-bold",
                style.pill,
              )}
            >
              {style.pulse ? (
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-pistachio" />
              ) : (
                StatusIcon && <StatusIcon className="h-3 w-3" />
              )}
              {getStatusLabel(order)}
            </span>
            {order.payed ? (
              <span className="inline-flex items-center gap-1 rounded-md border border-brand-pistachio/40 bg-brand-pistachio/15 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
                <Check className="h-3 w-3" />
                Pago
              </span>
            ) : (
              !closed && (
                <span className="inline-flex items-center rounded-md border border-terra/25 bg-terra-soft px-1.5 py-0.5 text-[11px] font-semibold text-terra">
                  A receber
                </span>
              )
            )}
          </div>
          <div className="flex-shrink-0 text-right">
            <span className="block font-display text-lg font-extrabold leading-none tracking-tight text-primary">
              {formatCurrency(orderService.calculateTotal(order))}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {order.pay_method || "Pagamento não informado"}
            </span>
          </div>
        </div>

        {/* Cliente, tipo de entrega e tempo */}
        <div className="flex items-center justify-between gap-2 text-[13px]">
          <div className="flex min-w-0 items-center gap-1.5 font-semibold text-foreground">
            <User className="h-4 w-4 flex-shrink-0 text-primary" />
            <span className="truncate" title={order.client?.name}>
              {order.client?.name || "Cliente não informado"}
            </span>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-foreground">
              {delivery ? <Truck className="h-3 w-3" /> : <Store className="h-3 w-3" />}
              {delivery ? "Delivery" : "Retirada"}
              {delivery && freight > 0 && (
                <span className="font-normal text-muted-foreground">
                  +{formatCurrency(freight)}
                </span>
              )}
            </span>
            <span title={order.date_time}>
              • {createdAt ? formatElapsed(createdAt, now) : order.date_time || "—"}
            </span>
          </div>
        </div>

        {/* Prévia dos itens */}
        <button
          type="button"
          onClick={() => onDetails(order)}
          className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-surface px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted"
          aria-label={`Ver itens do pedido ${order.code}`}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="flex-shrink-0 font-bold text-primary">
              {itemCount} {itemCount === 1 ? "item" : "itens"}:
            </span>
            <span className="truncate text-muted-foreground" title={items}>
              {items || "Sem itens"}
            </span>
          </span>
          <span className="flex flex-shrink-0 items-center gap-1.5 text-[11px] font-bold text-primary">
            {order.coupon_code && (
              <span className="inline-flex items-center gap-0.5 rounded bg-secondary px-1 text-[10px] font-semibold">
                <Ticket className="h-3 w-3" />
                {order.coupon_code}
              </span>
            )}
            Ver
          </span>
        </button>

        {/* Etapa e progresso (pedidos no fluxo) */}
        {progress !== null && (
          <div className="flex flex-col gap-1 pt-0.5">
            <div className="flex items-center justify-between text-[11px] font-medium">
              <span className="flex items-center gap-1 text-muted-foreground">
                <ChefHat className="h-3.5 w-3.5 text-primary" />
                Etapa {FLOW.indexOf(order.status) + 1}/{FLOW.length}: {getStageLabel(order)}
              </span>
              {elapsed !== null && (
                <span
                  className={cn("font-bold", late ? "text-terra" : "text-primary")}
                  title={late ? `Em aberto há mais de ${LATE_AFTER_MINUTES} min` : undefined}
                >
                  {elapsed} min
                </span>
              )}
            </div>
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-surface-high"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={FLOW.length}
              aria-valuenow={FLOW.indexOf(order.status) + 1}
              aria-label="Progresso do pedido"
            >
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  late ? "bg-terra" : "bg-primary",
                )}
                style={{ width: `${progress * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Ações */}
        <div className="flex items-center gap-1.5 pt-1">
          {next ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onChangeStatus(order, next.status)}
              title={`Mudar para "${next.status}"`}
              className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-bold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <NextIcon className="h-4 w-4" />}
              {busy ? "Salvando..." : next.label}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onDetails(order)}
              className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-semibold text-primary transition-colors hover:bg-muted"
            >
              <Eye className="h-4 w-4" />
              Ver detalhes
            </button>
          )}
          {!order.payed && !closed && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onPayment(order)}
              aria-label={`Confirmar pagamento do pedido ${order.code}`}
              title="Confirmar pagamento"
              className={iconButton}
            >
              <Wallet className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onWhatsApp(order)}
            aria-label={`WhatsApp de ${order.client?.name || "cliente"}`}
            title="WhatsApp do cliente"
            className={iconButton}
          >
            <MessageCircle className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onPrint(order)}
            aria-label={`Imprimir comanda do pedido ${order.code}`}
            title="Imprimir comanda"
            className={iconButton}
          >
            <Printer className="h-4 w-4" />
          </button>
          {/* modal={false}: itens abrem Dialog/AlertDialog; com menu modal o Radix
              pode deixar o body travado (pointer-events: none) */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={busy}
                aria-label={`Mais ações do pedido ${order.code}`}
                title="Mais ações"
                className={iconButton}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onSelect={() => onDetails(order)}>
                <Eye className="mr-2 h-4 w-4" />
                Ver detalhes
              </DropdownMenuItem>
              {order.status !== "Cancelado" && (
                <DropdownMenuItem onSelect={() => onPayment(order)}>
                  <Wallet className="mr-2 h-4 w-4" />
                  {order.payed ? "Registrar pagamento" : "Confirmar pagamento"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => onStorePrinter(order)}>
                <Send className="mr-2 h-4 w-4" />
                Enviar à impressora da loja
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onCopyText(order)}>
                <Copy className="mr-2 h-4 w-4" />
                Copiar texto da comanda
              </DropdownMenuItem>
              {order.status !== "Cancelado" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Mudar status
                  </DropdownMenuLabel>
                  {[...FLOW.slice(1), "Finalizado"]
                    .filter((status) => status !== order.status)
                    .map((status) => {
                      const Icon = STEP_ICONS[status];
                      return (
                        <DropdownMenuItem
                          key={status}
                          onSelect={() => onChangeStatus(order, status)}
                        >
                          <Icon className="mr-2 h-4 w-4" />
                          {status}
                        </DropdownMenuItem>
                      );
                    })}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => onCancel(order)}
                    className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                  >
                    <Ban className="mr-2 h-4 w-4" />
                    Cancelar pedido
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </article>
  );
};

export default OrderListCard;
